<template>
    <div class="modal fade show rx-product-modal" aria-labelledby="rxProductModalTitle">
        <div class="modal-dialog modal-dialog-centered modal-md">
            <div class="modal-content">
                <div class="row">
                    <div class="col-12">
                        <div class="d-flex justify-content-between align-items-center px-1 my-1">
                            <h1 class="mb-0">Add Product</h1>
                            <button
                                @click="$emit('onClose')"
                                type="button"
                                class="btn-close"
                                aria-label="Close"
                            ></button>
                        </div>
                    </div>
                </div>
                <hr class="mt-0">
                <div class="modal-body px-3 pb-3">
                    <div class="mb-3">
                        <label class="form-label" for="rx-product-name">Product Name</label>
                        <input
                            v-model="productName"
                            type="text"
                            id="rx-product-name"
                            class="form-control"
                            placeholder="Enter product name"
                            @keyup.enter="submit"
                        />
                        <span v-if="nameError" class="invalid-feedback d-block">{{ nameError }}</span>
                    </div>
                    <div class="text-center">
                        <button
                            @click="submit"
                            :disabled="loading"
                            type="button"
                            class="btn btn-primary me-1 waves-effect waves-float waves-light"
                        >
                            <span v-if="loading" class="spinner-border spinner-border-sm text-light me-1" role="status"></span>
                            Submit
                        </button>
                        <button
                            @click="$emit('onClose')"
                            type="button"
                            class="btn btn-outline-secondary waves-effect"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { inject, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import handleInventory from '@/services/modules/inventory';

const emit = defineEmits(['onClose', 'onProductAdded']);

const route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');

const { fetchUnit, fetchGroup, fetchAccountHeads, fetchAccountHeadsBySlags, storeProduct } = handleInventory();

const productName = ref('');
const nameError = ref('');
const loading = ref(false);
const units = ref([]);
const groups = ref([]);
const accountHeads = ref([]);
const slagHeads = ref({});

const fetchDependencies = async () => {
    const companyId = route.params.companyId;
    const slags = ['purchase_accounts', 'sales_accounts', 'cost_of_sales'];
    const slagQuery = `?company_id=${companyId}` + slags.map(s => `&slags[]=${s}`).join('');

    const [unitRes, groupRes, accountRes, slagRes] = await Promise.all([
        fetchUnit(companyId),
        fetchGroup(companyId, 'finished_goods'),
        fetchAccountHeads(companyId),
        fetchAccountHeadsBySlags(slagQuery),
    ]);
    if (unitRes.data) units.value = unitRes.data;
    if (groupRes.data) groups.value = groupRes.data;
    if (accountRes.data) accountHeads.value = accountRes.data;
    if (slagRes.data) slagHeads.value = slagRes.data;
}

const submit = async () => {
    nameError.value = '';
    if (!productName.value || productName.value.trim().length < 3) {
        nameError.value = 'Product name must be at least 3 characters';
        return;
    }

    const firstGroupId = groups.value.length ? groups.value[0].id : null;
    const firstUnitId = units.value.length ? units.value[0].id : null;

    if (!firstGroupId || !firstUnitId) {
        showError('Unable to determine product group or unit. Please try again.');
        return;
    }

    const sh = slagHeads.value || {};
    const ah = accountHeads.value || [];

    const purchaseAccountId =
        sh.purchase_accounts && sh.purchase_accounts.length
            ? sh.purchase_accounts[0].id
            : null;

    const salesAccountId =
        sh.sales_accounts && sh.sales_accounts.length
            ? sh.sales_accounts[0].id
            : null;

    const costOfSalesAccountId =
        sh.cost_of_sales && sh.cost_of_sales.length
            ? sh.cost_of_sales[0].id
            : null;

    const inventoryAccount = ah.find(function (h) {
      return h.account_slug === 'inventory_assets';
    });

    const inventoryAccountId = inventoryAccount ? inventoryAccount.id : null;

    loading.value = true;
    try {
        const fd = new FormData();
        fd.append('company_id', route.params.companyId);
        fd.append('name', productName.value.trim());
        fd.append('product_head_id', firstGroupId);
        fd.append('unit_id', firstUnitId);
        fd.append('status', 'active');
        fd.append('inventory_method', 'fifo');
        fd.append('specifications', JSON.stringify({}));
        fd.append('alternative_unit_ids', JSON.stringify([]));
        if (purchaseAccountId) fd.append('purchase_account_head_id', purchaseAccountId);
        if (salesAccountId) fd.append('sales_account_head_id', salesAccountId);
        if (costOfSalesAccountId) fd.append('cost_of_sales_account_head_id', costOfSalesAccountId);
        if (inventoryAccountId) fd.append('inventory_account_head_id', inventoryAccountId);

        const res = await storeProduct(fd);
        if (!res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        emit('onProductAdded', res.data);
    } catch (err) {
        showError('Something went wrong. Please try again.');
    } finally {
        loading.value = false;
    }
}

onMounted(fetchDependencies);
</script>

<style scoped>
.rx-product-modal {
    display: block;
    background: #1c1d1c33;
    z-index: 1060;
}
</style>
